import { z } from "zod";
import { normalizeBdMobile } from "../phone";

export const bdMobileSchema = z
  .string()
  .trim()
  .min(10)
  .max(20)
  .transform((value, ctx) => {
    const normalized = normalizeBdMobile(value);
    if (!normalized) {
      ctx.addIssue({ code: "custom", message: "Enter a valid Bangladeshi mobile number" });
      return z.NEVER;
    }
    return normalized;
  });

export const localeSchema = z.enum(["bn", "en"]);
export type Locale = z.infer<typeof localeSchema>;

/**
 * Gmail address that keys an account. Gmail ignores dots and anything after "+" in the local part,
 * so those are dropped from the key: a.b+shop@gmail.com and ab@gmail.com are one mailbox.
 */
export function gmailKey(email: string): string {
  const [local = "", domain = ""] = email.trim().toLowerCase().split("@");
  return `${local.split("+")[0]?.replaceAll(".", "") ?? ""}@${domain}`;
}

export const gmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254)
  .transform((v) => v.replace(/@googlemail\.com$/, "@gmail.com"))
  .pipe(
    z
      .email("Enter a valid email address")
      .refine((v) => v.endsWith("@gmail.com"), "Use a Gmail address (ending in @gmail.com)")
      .refine((v) => gmailKey(v).length > "@gmail.com".length + 5, "Enter a valid Gmail address"),
  );

const turnstileToken = z.string().min(1).max(4096);

/** Sign-in code by Gmail; older accounts may still ask for one by their phone. */
export const otpRequestSchema = z.union([
  z.object({ email: gmailSchema, turnstileToken, locale: localeSchema.default("bn") }),
  z.object({ phone: bdMobileSchema, turnstileToken, locale: localeSchema.default("bn") }),
]);
export type OtpRequestInput = z.input<typeof otpRequestSchema>;

const COMMON_PASSWORDS = new Set([
  "1234567890",
  "12345678910",
  "123456789a",
  "1234567890a",
  "password123",
  "password1234",
  "qwertyuiop",
  "1q2w3e4r5t",
  "bangladesh",
  "bangladesh123",
  "iloveyou123",
  "0123456789",
  "1111111111",
  "abcdefghij",
  "asdfghjkl1",
  "zeeta12345",
]);

export const passwordSchema = z
  .string()
  .min(10, "Use at least 10 characters")
  .max(128, "Use at most 128 characters")
  .refine((v) => !COMMON_PASSWORDS.has(v.toLowerCase()), "This password is too common")
  .refine((v) => new Set(v).size >= 4, "This password is too simple");

export const passwordLoginSchema = z.object({
  email: gmailSchema,
  password: z.string().min(1).max(128),
  trustDevice: z.boolean().default(false),
  turnstileToken,
});
export type PasswordLoginInput = z.input<typeof passwordLoginSchema>;

export const passwordSetSchema = z.object({
  /** Needed to change an existing password. */
  currentPassword: z.string().max(128).optional(),
  password: passwordSchema,
});
export type PasswordSetInput = z.input<typeof passwordSetSchema>;

/** "Forgot password": a code to the account's Gmail. */
export const passwordForgotSchema = z.object({
  email: gmailSchema,
  turnstileToken,
  locale: localeSchema.default("bn"),
});
export type PasswordForgotInput = z.input<typeof passwordForgotSchema>;

/** The emailed code and the new password. */
export const passwordResetSchema = z.object({
  challengeId: z.uuid(),
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
  password: passwordSchema,
  trustDevice: z.boolean().default(false),
});
export type PasswordResetInput = z.input<typeof passwordResetSchema>;

export const otpVerifySchema = z.object({
  challengeId: z.uuid(),
  code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code"),
  trustDevice: z.boolean().default(false),
});
export type OtpVerifyInput = z.input<typeof otpVerifySchema>;

export const profileUpdateSchema = z
  .object({
    name: z.string().trim().min(2).max(80).optional(),
    locale: localeSchema.optional(),
    /** Optional contact number; verified separately by SMS. null removes it. */
    phone: bdMobileSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");
export type ProfileUpdateInput = z.input<typeof profileUpdateSchema>;

export const meSchema = z.object({
  user: z.object({
    id: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    phoneVerified: z.boolean(),
    name: z.string().nullable(),
    locale: localeSchema,
    hasPassword: z.boolean(),
    google: z.boolean(),
    /** Signed in with a temporary password: must choose their own before going on. */
    mustChangePassword: z.boolean().optional(),
  }),
  memberships: z.array(
    z.object({
      tenantId: z.string(),
      tenantName: z.string(),
      tenantSlug: z.string(),
      tenantStatus: z.enum(["provisioning", "active", "suspended", "closed"]),
      role: z.string(),
    }),
  ),
  /** Zeetabyte staff only: their platform admin role. */
  platformRole: z.enum(["admin", "support"]).nullable().optional(),
});
export type Me = z.infer<typeof meSchema>;
